import {useState, useMemo, useCallback, useEffect} from "react"
import {useIoTShadowStore} from "../store/iotShadowStore"
import {useDevicesStore} from "../store/devicesStore"
import {formatVersionDate, getDeviceType} from "../utils/versionUtils"
import {useFavorites} from "./useFavorites"
import {useIsAdmin} from "../store/authStore"

type ViewMode = "cards" | "table"

export const useDeviceDashboard = () => {
	const {
		devices: shadowDevices,
		devicesList: userDevices,
		isLoading: shadowLoading,
		error: shadowError,
		isLiveMode,
		liveUpdateInterval,
		fetchDevices,
		fetchDeviceShadow,
		fetchMultipleDeviceShadows,
		startLiveUpdates,
		stopLiveUpdates,
		setLiveUpdateInterval,
		clearError: clearShadowError,
		clearDevices: clearShadowDevices,
		clearAllAlarms,
	} = useIoTShadowStore()

	const {
		deviceConnectivity,
		connectivityLoading,
		connectivityError,
		firmwareUploadLoading,
		firmwareUploadError,
		firmwareUploadSuccess,
		rtlUploadLoading,
		rtlUploadError,
		rtlUploadSuccess,
		latestFirmware,
		jobProgress,
		currentJobId,
		fetchDevicesConnectivity,
		clearConnectivity,
		uploadFirmware,
		uploadFirmwarePerDevice,
		uploadRtl,
		uploadRtlPerDevice,
		uploadRtlFromCi,
		clearFirmwareUploadStatus,
		clearRtlUploadStatus,
		fetchLatestFirmware,
		getDeviceFirmware,
		getDeviceJobStatus,
		checkJobProgress,
		stopJobProgressPolling,
		cancelQueuedJobs,
		cancelInProgressJobs,
		cancelDeviceJobs,
	} = useDevicesStore()

	const isAdmin = useIsAdmin()

	const [customInterval, setCustomInterval] = useState(liveUpdateInterval)
	const [hasInitializedDevices, setHasInitializedDevices] = useState(false)
	const [viewMode, setViewMode] = useState<ViewMode>("table")
	const [selectedFile, setSelectedFile] = useState<File | null>(null)
	const [selectedRtlFile, setSelectedRtlFile] = useState<File | null>(null)
	const [selectedDevices, setSelectedDevices] = useState<string[]>([])
	const [cardsPerRow, setCardsPerRow] = useState(3)
	const [showFavoritesOnly, setShowFavoritesOnly] = useState(false)
	const {favorites} = useFavorites()

	const isLoading = shadowLoading || connectivityLoading
	const macAddresses = useMemo(() => userDevices.map((device) => device.device_uid), [userDevices])

	const deviceStats = useMemo(() => {
		const totalDevices = userDevices.length
		const connectedDevices = Array.from(deviceConnectivity.values()).filter(
			(connectivity) => connectivity.status === "connected",
		).length
		const disconnectedDevices = Array.from(deviceConnectivity.values()).filter(
			(connectivity) => connectivity.status === "disconnected",
		).length

		return {
			total: totalDevices,
			online: connectedDevices,
			offline: disconnectedDevices,
		}
	}, [userDevices.length, deviceConnectivity])

	const stableFetchDevices = useCallback(() => {
		fetchDevices()
	}, [fetchDevices])

	const stableFetchShadows = useCallback(
		(addresses: string[]) => {
			if (addresses.length > 0) {
				fetchMultipleDeviceShadows(addresses)
			}
		},
		[fetchMultipleDeviceShadows],
	)

	const stableFetchConnectivity = useCallback(
		(addresses: string[]) => {
			if (addresses.length > 0) {
				fetchDevicesConnectivity(addresses)
			}
		},
		[fetchDevicesConnectivity],
	)

	useEffect(() => {
		if (!hasInitializedDevices) {
			stableFetchDevices()
			fetchLatestFirmware()
			setHasInitializedDevices(true)
		}

		return () => {
			stopLiveUpdates()
		}
	}, [stableFetchDevices, hasInitializedDevices, stopLiveUpdates, fetchLatestFirmware])

	useEffect(() => {
		if (macAddresses.length > 0 && !isLiveMode) {
			stableFetchShadows(macAddresses)
			stableFetchConnectivity(macAddresses)
		}
	}, [stableFetchShadows, stableFetchConnectivity, isLiveMode, macAddresses])

	const handleRefreshDevice = useCallback(
		(deviceId: string) => {
			fetchDeviceShadow(deviceId)
			fetchDevicesConnectivity([deviceId])
		},
		[fetchDeviceShadow, fetchDevicesConnectivity],
	)

	const handleRefreshDevices = useCallback(() => {
		stableFetchDevices()
		if (macAddresses.length > 0) {
			stableFetchShadows(macAddresses)
			stableFetchConnectivity(macAddresses)
		}
		fetchLatestFirmware()
	}, [
		stableFetchDevices,
		macAddresses,
		stableFetchShadows,
		stableFetchConnectivity,
		fetchLatestFirmware,
	])

	const handleRefreshConnectivity = useCallback(() => {
		stableFetchConnectivity(macAddresses)
	}, [stableFetchConnectivity, macAddresses])

	const handleToggleLiveMode = useCallback(() => {
		if (isLiveMode) {
			stopLiveUpdates()
		} else {
			if (macAddresses.length > 0) {
				startLiveUpdates(macAddresses, liveUpdateInterval)
			}
		}
	}, [isLiveMode, stopLiveUpdates, macAddresses, startLiveUpdates, liveUpdateInterval])

	const handleIntervalChange = useCallback(
		(newInterval: number) => {
			setCustomInterval(newInterval)
			setLiveUpdateInterval(newInterval)
		},
		[setLiveUpdateInterval],
	)

	const handleClearError = useCallback(() => {
		clearShadowError()
		clearFirmwareUploadStatus()
		clearRtlUploadStatus()
	}, [clearShadowError, clearFirmwareUploadStatus, clearRtlUploadStatus])

	const handleFileSelect = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0]
			if (file) {
				if (file.name.endsWith(".bin")) {
					setSelectedFile(file)
					clearFirmwareUploadStatus()
				} else {
					clearFirmwareUploadStatus()
					setSelectedFile(null)
				}
			}
		},
		[clearFirmwareUploadStatus],
	)

	const handleRtlFileSelect = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0]
			if (file) {
				if (file.name.endsWith(".bin")) {
					setSelectedRtlFile(file)
					clearRtlUploadStatus()
				} else {
					clearRtlUploadStatus()
					setSelectedRtlFile(null)
				}
			}
		},
		[clearRtlUploadStatus],
	)

	const handleFileUpload = useCallback(async () => {
		if (!selectedFile) return

		if (selectedDevices.length === 0) {
			// Developers cannot upload to ALL devices: they must pick their devices first.
			if (!isAdmin) {
				useDevicesStore.setState({
					firmwareUploadError: "Select one or more devices in the table first. Uploading to all devices is available to administrators only.",
				})
				return
			}
			const confirmed = window.confirm(
				"No devices selected. This will send the firmware update to ALL devices. Are you sure?",
			)
			if (!confirmed) return
		}

		try {
			const reader = new FileReader()

			reader.onload = async (e) => {
				try {
					const arrayBuffer = e.target?.result as ArrayBuffer
					const base64 = btoa(
						new Uint8Array(arrayBuffer).reduce(
							(data, byte) => data + String.fromCharCode(byte),
							"",
						),
					)

					if (selectedDevices.length > 0) {
						await uploadFirmwarePerDevice(
							selectedFile.name,
							base64,
							"application/octet-stream",
							selectedDevices,
						)
					} else {
						await uploadFirmware(selectedFile.name, base64, "application/octet-stream")
					}
					setSelectedFile(null)
					setSelectedDevices([])
					const fileInput = document.getElementById("binFileInput") as HTMLInputElement
					if (fileInput) fileInput.value = ""

					fetchLatestFirmware()
				} catch (error) {
					console.error("Upload error:", error)
				}
			}

			reader.onerror = () => {
				console.error("Failed to read file")
			}

			reader.readAsArrayBuffer(selectedFile)
		} catch (error) {
			console.error("File processing error:", error)
		}
	}, [selectedFile, selectedDevices, isAdmin, uploadFirmware, uploadFirmwarePerDevice, fetchLatestFirmware])

	const handleRtlFileUpload = useCallback(async () => {
		if (!selectedRtlFile) return

		if (selectedDevices.length === 0) {
			// Developers cannot upload to ALL devices: they must pick their devices first.
			if (!isAdmin) {
				useDevicesStore.setState({
					rtlUploadError: "Select one or more devices in the table first. Uploading to all devices is available to administrators only.",
				})
				return
			}
			const confirmed = window.confirm(
				"No devices selected. This will send the RTL update to ALL devices. Are you sure?",
			)
			if (!confirmed) return
		}

		try {
			const reader = new FileReader()

			reader.onload = async (e) => {
				try {
					const arrayBuffer = e.target?.result as ArrayBuffer
					const base64 = btoa(
						new Uint8Array(arrayBuffer).reduce(
							(data, byte) => data + String.fromCharCode(byte),
							"",
						),
					)

					if (selectedDevices.length > 0) {
						await uploadRtlPerDevice(
							selectedRtlFile.name,
							base64,
							"application/octet-stream",
							selectedDevices,
						)
					} else {
						await uploadRtl(selectedRtlFile.name, base64, "application/octet-stream")
					}
					setSelectedRtlFile(null)
					setSelectedDevices([])
					const fileInput = document.getElementById("rtlFileInput") as HTMLInputElement
					if (fileInput) fileInput.value = ""

					fetchLatestFirmware()
				} catch (error) {
					console.error("Upload error:", error)
				}
			}

			reader.onerror = () => {
				console.error("Failed to read file")
			}

			reader.readAsArrayBuffer(selectedRtlFile)
		} catch (error) {
			console.error("File processing error:", error)
		}
	}, [selectedRtlFile, selectedDevices, isAdmin, uploadRtl, uploadRtlPerDevice, fetchLatestFirmware])

	const shadowDeviceArray = useMemo(() => Array.from(shadowDevices.values()), [shadowDevices])

	const getDisplayConnectionStatus = useCallback(
		(deviceId: string) => {
			const connectivity = deviceConnectivity.get(deviceId)
			const status = (connectivity?.status || "unknown") as "connected" | "disconnected" | "unknown"

			return {
				status,
				timestamp: connectivity?.timestamp
					? new Date(connectivity.timestamp).toISOString()
					: undefined,
				displayText:
					status === "connected"
						? "Connected"
						: status === "disconnected"
							? "Disconnected"
							: "Unknown",
			}
		},
		[deviceConnectivity],
	)

	// Refreshes the job progress after a cancel. Non-fatal: the cancel itself already succeeded,
	// so a failing progress check must not be reported as a failed cancel.
	const refreshJobProgressAfterCancel = useCallback(async () => {
		const jobIdToCheck = currentJobId || useDevicesStore.getState().latestJobId
		if (!jobIdToCheck) return
		try {
			await checkJobProgress(jobIdToCheck, true)
		} catch (error) {
			console.error("Error refreshing job progress after cancel:", error)
		}
	}, [currentJobId, checkJobProgress])

	// Only errors of the cancel call itself propagate (ManualControls shows them).
	const handleCancelQueued = useCallback(async () => {
		try {
			await cancelQueuedJobs()
		} catch (error) {
			console.error("Error canceling queued jobs:", error)
			throw error
		}
		await refreshJobProgressAfterCancel()
	}, [cancelQueuedJobs, refreshJobProgressAfterCancel])

	const handleCancelInProgress = useCallback(async () => {
		try {
			await cancelInProgressJobs()
		} catch (error) {
			console.error("Error canceling in-progress jobs:", error)
			throw error
		}
		await refreshJobProgressAfterCancel()
	}, [cancelInProgressJobs, refreshJobProgressAfterCancel])

	const handleCancelDeviceJobs = useCallback(
		async (deviceIds: string[]) => {
			try {
				await cancelDeviceJobs(deviceIds)
			} catch (error) {
				console.error("Error canceling device jobs:", error)
				throw error
			}
			await refreshJobProgressAfterCancel()
		},
		[cancelDeviceJobs, refreshJobProgressAfterCancel],
	)

	const handleClearAllAlarms = useCallback(
		(deviceIds: string[]) => clearAllAlarms(deviceIds),
		[clearAllAlarms],
	)

	const handleClearAll = useCallback(() => {
		clearShadowDevices()
		clearConnectivity()
		setSelectedDevices([])
	}, [clearShadowDevices, clearConnectivity])

	const parseRadarFirmwareNumbers = (radarFirmware: string): number[] => {
		if (!radarFirmware) return []
		try {
			if (radarFirmware.startsWith("[") && radarFirmware.endsWith("]")) {
				return JSON.parse(radarFirmware).filter(
					(v: unknown) => typeof v === "number" && !isNaN(v as number),
				)
			} else if (radarFirmware.includes(",")) {
				return radarFirmware
					.split(",")
					.map((v) => parseInt(v.trim()))
					.filter((v) => !isNaN(v))
			} else {
				const single = parseInt(radarFirmware)
				return isNaN(single) ? [] : [single]
			}
		} catch {
			return []
		}
	}

	const formatRadarFirmwareVersions = useCallback(
		(radarFirmware: string | undefined, deviceId: string) => {
			if (!radarFirmware) return "N/A"

			const versions = parseRadarFirmwareNumbers(radarFirmware)
			if (versions.length === 0) return radarFirmware

			const deviceFirmware = getDeviceFirmware(deviceId)
			const latestVersion = deviceFirmware?.version || latestFirmware?.version

			return versions
				.map((version, key) => {
					const versionStr = formatVersionDate(version)
					const isLatest = latestVersion && versionStr === latestVersion
					const color = isLatest ? "text-green-600 font-semibold" : "text-orange-500 font-semibold"
					return `<span class="${color}">Radar ${key}: ${versionStr}</span>`
				})
				.join(", ")
		},
		[latestFirmware, getDeviceFirmware],
	)

	const isRadarVersionCurrent = useCallback(
		(radarFirmware: string | undefined, deviceId: string) => {
			if (!radarFirmware) return null

			const deviceFirmware = getDeviceFirmware(deviceId)
			const latestVersion = deviceFirmware?.version || latestFirmware?.version
			if (!latestVersion) return null

			const versions = parseRadarFirmwareNumbers(radarFirmware)
			if (versions.length === 0) return null

			return versions.map((version) => formatVersionDate(version) === latestVersion)
		},
		[latestFirmware, getDeviceFirmware],
	)

	const filteredDeviceArray = useMemo(() => {
		if (!showFavoritesOnly) return shadowDeviceArray
		return shadowDeviceArray.filter((device) => favorites.includes(device.deviceId))
	}, [shadowDeviceArray, showFavoritesOnly, favorites])

	const tableData = useMemo(() => {
		return userDevices.map((device) => {
			const shadowData = shadowDevices.get(device.device_uid)
			const connectivity = deviceConnectivity.get(device.device_uid)
			const reported = shadowData?.rawShadowData?.default?.state?.reported
			const radarFirmware = reported?.RADAR_FIRMWARE
			const jobStatus = getDeviceJobStatus(device.device_uid)

			return {
				macAddress: device.device_uid,
				connectionStatus: (connectivity?.status || "unknown") as
					| "connected"
					| "disconnected"
					| "unknown",
				lastSeen: connectivity?.timestamp
					? new Date(connectivity.timestamp).toISOString()
					: undefined,
				dataSize: shadowData?.rawShadowData
					? JSON.stringify(shadowData.rawShadowData).length
					: undefined,
				softwareVersion: reported?.FW_VERSION || "",
				fwStatus: reported?.FW_STATUS || "N/A",
				radarFirmwareVersions: formatRadarFirmwareVersions(radarFirmware, device.device_uid),
				radarFirmwareStatus: isRadarVersionCurrent(radarFirmware, device.device_uid),
				radarFwStatus: reported?.RADAR_FW_STATUS || "N/A",
				shadowClassic: shadowData?.rawShadowData?.default || null,
				shadowRadar: shadowData?.rawShadowData?.radar || null,
				uploadStatus: jobStatus?.status,
				uploadError: jobStatus?.error,
			}
		})
	}, [
		userDevices,
		shadowDevices,
		deviceConnectivity,
		formatRadarFirmwareVersions,
		isRadarVersionCurrent,
		getDeviceJobStatus,
	])

	// Type of the device when exactly one is selected (decides the CI build folder for an RTL upload
	// from S3). Same rule as the A/F symbol in the table (FW_VERSION in the shadow); falls back to the
	// device record's device_type while the shadow is not loaded yet.
	const singleSelectedDeviceType = useMemo((): "angel" | "falcon" | "unknown" | null => {
		if (selectedDevices.length !== 1) return null
		const mac = selectedDevices[0]
		const row = tableData.find((r) => r.macAddress === mac)
		const fromShadow = row ? getDeviceType(row.softwareVersion) : "unknown"
		if (fromShadow !== "unknown") return fromShadow
		const recordType = String(userDevices.find((d) => d.device_uid === mac)?.device_type || "").toLowerCase()
		if (recordType === "angel" || recordType === "falcon") return recordType
		return "unknown"
	}, [selectedDevices, tableData, userDevices])

	// RTL upload of a CI build (S3) to the single selected device: the server copies the build.
	const handleRtlCiUpload = useCallback(
		async (key: string) => {
			if (selectedDevices.length !== 1) {
				useDevicesStore.setState({
					rtlUploadError: "Uploading a CI build from S3 is possible for exactly one selected device.",
					rtlUploadSuccess: null,
				})
				return
			}
			try {
				await uploadRtlFromCi(selectedDevices[0], key)
				setSelectedDevices([])
				fetchLatestFirmware()
			} catch (error) {
				console.error("Upload error:", error)
			}
		},
		[selectedDevices, uploadRtlFromCi, fetchLatestFirmware],
	)

	const handleDeviceSelectionChange = useCallback((deviceIds: string[]) => {
		setSelectedDevices(deviceIds)
	}, [])

	return {
		shadowError,
		connectivityError,
		isLiveMode,
		liveUpdateInterval,
		customInterval,
		viewMode,
		setViewMode,
		selectedFile,
		selectedRtlFile,
		selectedDevices,
		cardsPerRow,
		setCardsPerRow,
		showFavoritesOnly,
		setShowFavoritesOnly,
		isLoading,
		connectivityLoading,
		firmwareUploadLoading,
		firmwareUploadError,
		firmwareUploadSuccess,
		rtlUploadLoading,
		rtlUploadError,
		rtlUploadSuccess,
		latestFirmware,
		jobProgress,
		currentJobId,
		macAddresses,
		deviceStats,
		filteredDeviceArray,
		tableData,
		userDevices,
		stopJobProgressPolling,
		clearFirmwareUploadStatus,
		clearRtlUploadStatus,
		getDisplayConnectionStatus,
		handleRefreshDevice,
		handleRefreshDevices,
		handleRefreshConnectivity,
		handleToggleLiveMode,
		handleIntervalChange,
		handleClearError,
		handleFileSelect,
		handleRtlFileSelect,
		handleFileUpload,
		handleRtlFileUpload,
		handleRtlCiUpload,
		singleSelectedDeviceType,
		handleCancelQueued,
		handleCancelInProgress,
		handleCancelDeviceJobs,
		handleClearAllAlarms,
		handleClearAll,
		handleDeviceSelectionChange,
	}
}
